/**
 * A file Oliver produced, written as `![What it shows](attachment:2)` for a chart
 * or `[invoices.csv](attachment:2)` for anything you download.
 *
 * The reference is a POSITION for most of its life, not an id: the backend hands
 * Oliver `attachment:2` because a model asked to transcribe a uuid gets one wrong
 * often enough to lose a chart, and expands it only once the answer is composed.
 * So this must match the handle form too — demanding a uuid meant a streaming
 * reference never matched, reached `marked` untouched, and rendered as
 * `<img src="attachment:2">`: a broken image re-requested on every token.
 *
 * The leading `!` is OPTIONAL and no longer decides how it renders. It used to be
 * required, so only images matched: a generated spreadsheet written as a link was
 * never resolved and rendered as a dead href, while one written as an image
 * resolved and then rendered as a broken-image icon. The MIME type decides now,
 * which is the only thing that actually knows — except while the reference is
 * still a handle, when there is no row to ask and the `!` is all there is.
 */
export const ATTACHMENT_REFERENCE =
  /(!?)\[([^\]]*)\]\(\s*attachment:([^)\s]+)\s*\)/gi;

/**
 * A reference written outside any node, as older answers hold it. A node and an inline
 * citation `*(attachment:…)*` are matched too, only so they are left as they are.
 */
const BARE_ATTACHMENT_REFERENCE = new RegExp(
  `${ATTACHMENT_REFERENCE.source}|\\*\\(attachment:[^)]*\\)\\*|attachment:([0-9a-zA-Z-]+)`,
  "gi"
);

/**
 * A reference written outside any node, as older answers hold it, made a node with no
 * label: it then renders and copies like every other file, under the file's own name.
 */
export function bareAsNodes(content) {
  return content.replace(
    BARE_ATTACHMENT_REFERENCE,
    (match, _bang, _label, node, ref) =>
      ref === undefined ? match : `[](attachment:${ref})`
  );
}

/** An expanded reference. Anything else is still a handle and cannot be looked up. */
const ATTACHMENT_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The real attachment id behind a reference, or null while it is still just
 * a handle.
 *
 * Oliver writes `attachment:2` — a position, not an id — because a model
 * asked to transcribe a uuid gets one wrong often enough to lose a chart.
 * The turn's ids arrive on their own event before the first token, so a
 * handle resolves mid-stream; a saved answer already carries real ids.
 */
export function attachmentIdFor(reference, artifacts) {
  if (ATTACHMENT_ID.test(reference)) return reference;
  if (!/^\d+$/.test(reference)) return null;
  return (artifacts || [])[Number(reference) - 1] || null;
}

/**
 * Any markdown image, whatever produced its URL. Only Oliver's own files are ever
 * fetched or shown (`isOliverFile`); a URL the model wrote itself could carry what
 * is on screen to anyone's server.
 */
export const MARKDOWN_IMAGE = /!\[([^\]]*)\]\((https?:\/\/[^)\s]+)\)/g;
